import { ChangeDetectionStrategy, Component, computed, inject, Signal } from '@angular/core';
import { OrIconComponent, PageActionsComponent, PageLayoutComponent, TitileNavbarComponent } from '@ui-components';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AppRoutes, NAVIGATION_TOKEN, NavigationProvider, PreventMultiClickDirective, TeamsRoutes } from '@core';
import { CreateTeamCommandHandler, CreateTeamCommandResult } from '../../application/handlers';
import { Observable, switchMap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'lib-create-team',
  templateUrl: './create-team.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TitileNavbarComponent,
    FormsModule,
    ReactiveFormsModule,
    PreventMultiClickDirective,
    PageLayoutComponent,
    PageActionsComponent,
    OrIconComponent
  ],
  providers: [CreateTeamCommandHandler]
})
export class CreateTeamComponent {
  private readonly createTeamCommandHandler: CreateTeamCommandHandler = inject(CreateTeamCommandHandler);
  private readonly navigationProvider: NavigationProvider = inject(NAVIGATION_TOKEN);

  readonly createTeamForm: FormGroup = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.maxLength(25)])
  });

  private readonly nameValue: Signal<string | undefined> = toSignal(
    this.createTeamForm.get('name')!.valueChanges as Observable<string>
  );

  readonly isNameOverAvailableLength: Signal<boolean> = computed(() => {
    const name: string | undefined = this.nameValue();
    return !!name && name.length > 25;
  });

  isFieldInvalid(fieldName: string): boolean {
    const control: AbstractControl<unknown, unknown> | null = this.createTeamForm.get(fieldName);
    return !!control?.touched && !!control?.invalid;
  }

  createTeam(): void {
    if (this.createTeamForm.invalid) return;

    const teamName: string = this.createTeamForm.get('name')?.value;

    this.createTeamCommandHandler
      .create({ teamName: teamName })
      .pipe(
        switchMap((data: CreateTeamCommandResult) =>
          this.navigationProvider.replace(
            `${AppRoutes.TEAMS}/${data.teamId}/${data.invitationToken}/${TeamsRoutes.CREATE_FINISH}`
          )
        )
      )
      .subscribe();
  }
}
